/**
 * Centralized Help Content System
 * All help documentation content in one structured location
 * Easy to update, extend, and search
 */

// ============== PAGE EXPLANATIONS ==============

export const pageExplanations = {
  executiveOverview: {
    id: 'executive-overview',
    title: 'Vue d\'Ensemble',
    category: 'pages',
    tags: ['portfolio', 'résumé', 'tableau de bord', 'métriques', 'aperçu'],
    what: 'Votre centre de commande du portefeuille. Voyez le capital total déployé, les deals actifs et les rendements agrégés en un coup d\'œil.',
    why: 'Prenez des décisions d\'investissement de haut niveau sans entrer dans les détails de chaque deal. Identifiez les tendances et les problèmes d\'allocation à l\'échelle du portefeuille.',
    metrics: [
      { name: 'Deals Actifs', description: 'Deals actuellement en cours (non vendus ou abandonnés)' },
      { name: 'Capital Déployé', description: 'Argent total investi sur tous les deals actifs' },
      { name: 'ROI Moyen', description: 'Rendement moyen pondéré de votre portefeuille' },
      { name: 'Valeur Pipeline', description: 'Valeur potentielle des deals en sourcing/négociation' },
    ],
    howToUse: 'Consultez chaque semaine pour suivre la santé du portefeuille. Cliquez sur une métrique pour approfondir. Utilisez la répartition par étape pour identifier les goulots d\'étranglement.',
    commonMistakes: [
      'Se concentrer uniquement sur le ROI sans considérer le capital à risque',
      'Ignorer les deals bloqués trop longtemps dans les premières étapes',
    ],
    relatedModules: ['deal-pipeline', 'deal-analyzer'],
  },

  dealPipeline: {
    id: 'deal-pipeline',
    title: 'Pipeline des Deals',
    category: 'pages',
    tags: ['pipeline', 'deals', 'étapes', 'workflow', 'entonnoir'],
    what: 'Suivez chaque deal du sourcing initial jusqu\'à la vente finale. Voyez où se trouve chaque opportunité dans votre workflow d\'investissement.',
    why: 'Évitez que les deals ne stagnent. Identifiez les étapes avec des goulots d\'étranglement. Priorisez votre temps sur les deals les plus susceptibles de se conclure.',
    metrics: [
      { name: 'Étape', description: 'Position actuelle dans le workflow à 10 étapes' },
      { name: 'Jours dans l\'Étape', description: 'Temps écoulé depuis la dernière transition' },
      { name: 'ROI Cible', description: 'Rendement projeté si exécuté selon le plan' },
      { name: 'Niveau de Risque', description: 'Score de risque évalué par l\'IA' },
    ],
    howToUse: 'Filtrez par étape pour concentrer votre travail. Triez par jours-dans-étape pour trouver les deals bloqués. Cliquez sur un deal pour l\'analyse complète.',
    commonMistakes: [
      'Laisser les deals stagner en "Souscription" trop longtemps sans décision',
      'Ne pas mettre à jour les étapes quand les jalons sont atteints',
    ],
    relatedModules: ['deal-detail', 'ai-intelligence'],
  },

  dealDetail: {
    id: 'deal-detail',
    title: 'Détail du Deal',
    category: 'pages',
    tags: ['deal', 'analyse', 'acquisition', 'rénovation', 'sortie', 'profit'],
    what: 'Analyse complète d\'un deal : conditions d\'acquisition, périmètre de rénovation, stratégie de sortie, support comparable et scoring IA.',
    why: 'Prenez des décisions d\'achat/rejet éclairées. Comprenez exactement d\'où vient le profit et ce qui pourrait mal tourner.',
    metrics: [
      { name: 'Profit Net', description: 'Profit attendu après tous les coûts et frais' },
      { name: 'ROI', description: 'Rendement sur le capital total investi' },
      { name: 'Seuil Rentabilité', description: 'Prix de sortie minimum pour récupérer tous les coûts' },
      { name: 'Support Comps', description: 'Qualité de validation par les données de marché' },
    ],
    howToUse: 'Examinez tous les onglets avant de prendre des décisions d\'acquisition. Portez une attention particulière aux Facteurs de Risque et aux recommandations IA.',
    commonMistakes: [
      'Ignorer le risque de délai de rénovation',
      'Supposer des prix de sortie sans support comparable',
      'Sous-estimer les coûts de financement sur les détentions longues',
    ],
    relatedModules: ['deal-analyzer', 'comparables', 'exit-tracker'],
  },

  quickAnalyzer: {
    id: 'quick-analyzer',
    title: 'Analyseur Rapide',
    category: 'pages',
    tags: ['analyseur', 'rapide', 'calculer', 'scénario', 'offre'],
    what: 'Calcul rapide de deal sans créer un enregistrement complet. Entrez les chiffres, obtenez une analyse de profit instantanée et des recommandations d\'offre.',
    why: 'Filtrez les opportunités rapidement. Connaissez votre offre maximum avant les négociations. Comparez les scénarios instantanément.',
    metrics: [
      { name: 'Offre Max', description: 'Prix le plus élevé qui atteint encore votre ROI cible' },
      { name: 'Offre Recommandée', description: 'Offre optimale équilibrant profit et probabilité de closing' },
      { name: 'Prix Plancher', description: 'En dessous de ce prix, le deal devient trop risqué' },
      { name: 'Marge Brute', description: 'Profit en pourcentage du prix de sortie' },
    ],
    howToUse: 'Entrez les détails de la propriété et les hypothèses. Lancez les scénarios Conservateur/Base/Agressif. Utilisez l\'Offre Max comme plafond dans les négociations.',
    commonMistakes: [
      'Utiliser les chiffres du scénario agressif comme cas de base',
      'Oublier d\'inclure tous les coûts d\'acquisition (notaire, agence)',
    ],
    relatedModules: ['deal-detail', 'comparables'],
  },

  renovationControl: {
    id: 'renovation-control',
    title: 'Contrôle Rénovation',
    category: 'pages',
    tags: ['rénovation', 'budget', 'calendrier', 'coûts', 'suivi', 'écart'],
    what: 'Suivez les dépenses réelles de rénovation vs les budgets prévus. Surveillez les calendriers et voyez l\'impact financier des dépassements.',
    why: 'Les dérapages de coût et de délai de rénovation détruisent la marge. Détectez les problèmes tôt avant qu\'ils ne deviennent irrécupérables.',
    metrics: [
      { name: 'Écart Budget', description: 'Différence entre les coûts prévus et réels' },
      { name: 'Retard Calendrier', description: 'Jours de retard par rapport au planning initial' },
      { name: 'Intérêts Supplémentaires', description: 'Coût de financement additionnel dû aux retards' },
      { name: 'ROI Actualisé', description: 'Rendement projeté tenant compte des dépassements actuels' },
    ],
    howToUse: 'Mettez à jour les coûts réels chaque semaine. Examinez immédiatement les alertes d\'écart. Recalculez l\'économie du deal quand les dépassements dépassent 10%.',
    commonMistakes: [
      'Attendre la fin du projet pour mettre à jour les réels',
      'Ignorer les retards de calendrier (ils coûtent aussi de l\'argent)',
      'Ne pas ajuster les hypothèses de sortie quand le budget change',
    ],
    relatedModules: ['deal-detail', 'partner-scorecard'],
  },

  marketComparables: {
    id: 'market-comparables',
    title: 'Comparables de Marché',
    category: 'pages',
    tags: ['comparables', 'comps', 'marché', 'prix', 'valorisation', 'référence'],
    what: 'Base de données des transactions et annonces du marché. Validez vos hypothèses d\'acquisition et de sortie avec des données réelles.',
    why: 'Vos projections de profit ne valent que ce que valent vos hypothèses de prix. Les comps fournissent des preuves objectives du marché.',
    metrics: [
      { name: 'Prix/m²', description: 'Prix au mètre carré pour comparaison' },
      { name: 'Score Pertinence', description: 'Similarité de ce comp avec votre propriété cible' },
      { name: 'Type Observation', description: 'Prix demandé vs vente réelle vs référence' },
      { name: 'Confiance', description: 'Note de qualité et fiabilité des données' },
    ],
    howToUse: 'Filtrez par arrondissement et état. Concentrez-vous sur les transactions VENDUES pour les hypothèses de sortie. Vérifiez manuellement les comps quand les enjeux sont élevés.',
    commonMistakes: [
      'Utiliser les prix demandés comme s\'ils étaient des prix de vente',
      'Comparer des sorties rénovées à des comps non rénovés',
      'Se fier à un seul comp aberrant pour la valorisation',
    ],
    relatedModules: ['deal-detail', 'data-ingestion'],
  },

  exitTracker: {
    id: 'exit-tracker',
    title: 'Suivi de Sortie',
    category: 'pages',
    tags: ['sortie', 'vente', 'annonce', 'offres', 'marketing', 'jours sur marché'],
    what: 'Surveillez les propriétés de l\'annonce à la vente. Suivez les offres, les réductions de prix et le temps sur le marché.',
    why: 'Chaque jour sur le marché coûte de l\'argent. Prenez des décisions basées sur les données concernant les prix et l\'acceptation des offres.',
    metrics: [
      { name: 'Jours sur Marché', description: 'Temps depuis la première mise en annonce' },
      { name: 'Prix Actuel', description: 'Prix d\'annonce actif' },
      { name: 'Meilleure Offre', description: 'Offre la plus élevée reçue' },
      { name: 'Coût de Détention', description: 'Coût journalier de possession continue' },
    ],
    howToUse: 'Examinez chaque semaine quand les propriétés sont en vente. Utilisez l\'analyse des coûts de détention pour évaluer les décisions de réduction de prix.',
    commonMistakes: [
      'Attendre le prix demandé tout en perdant des intérêts',
      'Rejeter des offres sans calculer le vrai coût de l\'attente',
    ],
    relatedModules: ['deal-detail', 'ai-intelligence'],
  },

  partnerScorecard: {
    id: 'partner-scorecard',
    title: 'Scorecard Partenaires',
    category: 'pages',
    tags: ['partenaire', 'sourceur', 'chef de projet', 'performance', 'score', 'évaluation'],
    what: 'Analytique de performance pour les sourceurs et chefs de projet. Suivez qui délivre des résultats et qui crée du risque.',
    why: 'Vos partenaires impactent directement vos rendements. Identifiez les meilleurs performers pour grandir avec eux et les sous-performers à gérer.',
    metrics: [
      { name: 'Score Partenaire', description: 'Note de performance composite (0-100)' },
      { name: 'Précision Prévision', description: 'Correspondance entre projections et réalité' },
      { name: 'Discipline Budget', description: 'Historique de respect du budget' },
      { name: 'Retard Moyen', description: 'Dépassement de délai typique en jours' },
    ],
    howToUse: 'Consultez avant d\'assigner de nouveaux deals. Comparez les partenaires. Utilisez les données de performance dans les négociations de frais.',
    commonMistakes: [
      'Juger les partenaires sur un seul deal plutôt que sur les tendances',
      'Ignorer la performance calendrier (ne regarder que le budget)',
    ],
    relatedModules: ['deal-detail', 'renovation-control'],
  },

  aiIntelligence: {
    id: 'ai-intelligence',
    title: 'Intelligence IA',
    category: 'pages',
    tags: ['ia', 'score', 'risque', 'recommandation', 'analyse', 'décision'],
    what: 'Analyse de deal alimentée par l\'IA combinant tous les points de données en scores actionnables et recommandations.',
    why: 'Synthétisez des informations complexes en signaux clairs d\'achat/rejet. Détectez les risques que l\'examen manuel pourrait manquer.',
    metrics: [
      { name: 'Score Deal', description: 'Qualité globale du deal (0-100)' },
      { name: 'Score Risque', description: 'Niveau de risque agrégé (plus bas = mieux)' },
      { name: 'Confiance', description: 'Fiabilité de l\'analyse basée sur la qualité des données' },
      { name: 'Recommandation', description: 'Action suggérée par l\'IA (Acheter/Négocier/Passer)' },
    ],
    howToUse: 'Examinez le mémo IA avant les décisions finales. Vérifiez les détails des scores pour comprendre les préoccupations. Croisez les alertes avec l\'analyse manuelle.',
    commonMistakes: [
      'Suivre aveuglément l\'IA sans comprendre le raisonnement',
      'Ignorer les avertissements de faible confiance',
      'Écarter les alertes d\'attention sans investigation',
    ],
    relatedModules: ['deal-detail', 'comparables', 'partner-scorecard'],
  },

  dataIngestion: {
    id: 'data-ingestion',
    title: 'Import de Données',
    category: 'pages',
    tags: ['import', 'csv', 'données', 'sources', 'comparables', 'externe'],
    what: 'Importez des données de marché externes depuis des fichiers CSV ou d\'autres sources. Examinez et approuvez les enregistrements avant qu\'ils n\'entrent dans votre analyse.',
    why: 'Les bonnes décisions nécessitent de bonnes données. Construisez systématiquement votre base de comparables à partir de sources vérifiées.',
    metrics: [
      { name: 'Confiance Source', description: 'Note de fiabilité de la source de données' },
      { name: 'Complétude', description: 'Nombre de champs requis présents' },
      { name: 'Statut Doublon', description: 'Si l\'enregistrement existe déjà dans la base' },
      { name: 'Score Confiance', description: 'Note globale de qualité des données' },
    ],
    howToUse: 'Téléchargez les exports CSV des sites d\'annonces. Mappez soigneusement les colonnes. Examinez chaque enregistrement avant approbation.',
    commonMistakes: [
      'Importer sans vérifier la qualité des données',
      'Approuver des doublons qui faussent l\'analyse',
      'Confondre prix demandés et prix vendus lors de l\'import',
    ],
    relatedModules: ['comparables', 'deal-detail'],
  },
};

// ============== KEY CONCEPTS ==============

export const keyConcepts = {
  roi: {
    id: 'roi',
    title: 'Return on Investment (ROI)',
    category: 'concepts',
    tags: ['roi', 'return', 'profit', 'investment', 'percentage'],
    shortDescription: 'Profit as a percentage of capital invested.',
    explanation: `ROI measures how efficiently you deploy capital. A 15% ROI means you earn €15 for every €100 invested.

**Formula:** (Net Profit ÷ Total Investment) × 100

**What counts as investment:**
- Acquisition price
- Notaire and agency fees
- Renovation costs
- Financing costs
- Holding costs

**Why it matters:** ROI lets you compare deals of different sizes. A €50K profit on a €500K investment (10% ROI) is less efficient than €30K profit on a €200K investment (15% ROI).

**Target:** Most experienced Paris investors target 12-18% ROI for typical flip deals. Higher ROI usually means higher risk.`,
    relatedConcepts: ['margin', 'break-even', 'profit'],
  },

  margin: {
    id: 'margin',
    title: 'Gross Margin',
    category: 'concepts',
    tags: ['margin', 'profit', 'percentage', 'exit', 'spread'],
    shortDescription: 'Profit as a percentage of exit price.',
    explanation: `Margin shows how much of your sale price is profit after all costs.

**Formula:** (Net Profit ÷ Exit Price) × 100

**Why margin differs from ROI:**
- ROI measures return on YOUR money invested
- Margin measures profit relative to the sale

**Example:**
- Exit price: €800,000
- Total costs: €700,000
- Profit: €100,000
- Margin: 12.5% (100K ÷ 800K)
- ROI: 14.3% (if you invested €700K)

**Why it matters:** Margin tells you how much room you have for price negotiation. A 15% margin means you can reduce price by 15% and still break even.`,
    relatedConcepts: ['roi', 'break-even', 'profit'],
  },

  breakEven: {
    id: 'break-even',
    title: 'Break-even Price',
    category: 'concepts',
    tags: ['break-even', 'minimum', 'floor', 'costs', 'recovery'],
    shortDescription: 'Minimum exit price to recover all costs.',
    explanation: `Break-even is your absolute floor price—sell below this and you lose money.

**What it includes:**
- Acquisition price + fees
- Renovation costs
- Financing/holding costs
- Selling costs (agency, notaire)

**Why it matters:**
- Sets your walk-away point in negotiations
- Helps evaluate offers against your downside
- Shows how much margin you really have

**Warning signs:**
- Break-even above comp-supported exit = risky deal
- Break-even creeping up during renovation = cost overrun
- Offers below break-even = accept the loss or hold

**Best practice:** Calculate break-even BEFORE making an offer. Know your floor before you negotiate.`,
    relatedConcepts: ['roi', 'margin', 'max-bid'],
  },

  compBasedPricing: {
    id: 'comp-based-pricing',
    title: 'Comp-based Pricing',
    category: 'concepts',
    tags: ['comparables', 'pricing', 'valuation', 'market', 'evidence'],
    shortDescription: 'Using market transactions to validate price assumptions.',
    explanation: `Comp-based pricing grounds your assumptions in market reality rather than wishful thinking.

**Types of comps:**
- **Sold transactions:** Most reliable—actual closed deals
- **Asking prices:** Useful but typically 5-15% above sale prices
- **Benchmarks:** Market averages by area/condition

**Relevance factors:**
- Location (same arrondissement, ideally same quartier)
- Size (within 20% of target property)
- Condition (match renovation state)
- Recency (within 6-12 months)

**How to use:**
1. Find 3-5 relevant sold comps
2. Calculate €/m² range
3. Apply to your target surface
4. Use low/base/high scenarios

**Warning signs:**
- No sold comps within 12 months
- Exit assumptions above highest comp
- Relying on single comp for pricing`,
    relatedConcepts: ['market-comparables', 'confidence-score'],
  },

  scenarioAnalysis: {
    id: 'scenario-analysis',
    title: 'Scenario Analysis',
    category: 'concepts',
    tags: ['scenario', 'conservative', 'aggressive', 'base case', 'sensitivity'],
    shortDescription: 'Testing deal viability across optimistic and pessimistic assumptions.',
    explanation: `Scenario analysis tests whether a deal works when things don't go as planned.

**Three standard scenarios:**

**Conservative (-10% exit, +15% costs):**
- Lower exit price than expected
- Higher renovation costs
- If profitable here, deal has margin of safety

**Base Case (your assumptions):**
- Most likely outcome
- Basis for decision-making

**Aggressive (+10% exit, -10% costs):**
- Upside potential
- Don't make decisions based on this

**Decision framework:**
- Conservative profitable → Strong buy
- Only base case profitable → Proceed with caution
- Only aggressive profitable → Pass

**Best practice:** Run scenarios BEFORE committing. If conservative scenario is break-even or losing, you have no room for error.`,
    relatedConcepts: ['roi', 'risk-assessment', 'max-bid'],
  },

  forecastVsActual: {
    id: 'forecast-vs-actual',
    title: 'Forecast vs. Actual',
    category: 'concepts',
    tags: ['forecast', 'actual', 'variance', 'tracking', 'accuracy'],
    shortDescription: 'Comparing projected numbers to real results.',
    explanation: `Tracking forecast vs. actual reveals whether your assumptions are realistic and who delivers on promises.

**What to track:**
- Acquisition price (forecast vs. final)
- Renovation costs (budget vs. actual spend)
- Timeline (planned vs. actual days)
- Exit price (expected vs. sold price)

**Why it matters:**
- Identifies systematic biases in your estimates
- Reveals partner reliability
- Improves future forecasting

**Partner implications:**
- Sourcers: Did deals close at projected prices?
- Project managers: Did renovations hit budget and timeline?

**Warning signs:**
- Consistent underestimation of renovation costs
- Exit prices consistently below forecasts
- Same partner repeatedly missing targets`,
    relatedConcepts: ['partner-scorecard', 'renovation-control'],
  },

  confidenceScore: {
    id: 'confidence-score',
    title: 'Confidence Score',
    category: 'concepts',
    tags: ['confidence', 'reliability', 'data quality', 'trust'],
    shortDescription: 'How reliable your analysis is based on data quality.',
    explanation: `Confidence score indicates how much you should trust the analysis—not whether the deal is good.

**What affects confidence:**
- Comparable set strength (more/better comps = higher confidence)
- Data completeness (missing fields = lower confidence)
- Partner track record (proven partners = higher confidence)
- Scenario stability (similar results across scenarios = higher confidence)

**Score interpretation:**
- 80-100: Strong foundation, analysis is reliable
- 60-80: Adequate data, proceed with awareness
- 40-60: Significant gaps, validate independently
- Below 40: Insufficient data, do not rely on analysis

**How to improve confidence:**
- Add more verified comparables
- Complete missing deal data
- Verify assumptions independently

**Warning:** High deal score + low confidence = proceed with caution. The deal might look good, but the analysis could be wrong.`,
    relatedConcepts: ['ai-intelligence', 'risk-assessment'],
  },
};

// ============== WORKFLOW STEPS ==============

export const investmentWorkflow = {
  title: 'Investment Workflow',
  description: 'Step-by-step guide through the complete investment process.',
  steps: [
    {
      id: 'sourcing',
      number: 1,
      title: 'Deal Sourcing',
      description: 'Finding off-market opportunities through your network.',
      actions: [
        'Partners submit opportunities via Deal Pipeline',
        'Initial details: property address, asking price, condition',
        'Automatic entry into "Sourcing" stage',
      ],
      tools: ['Deal Pipeline', 'Partner Scorecard'],
      nextStep: 'screening',
    },
    {
      id: 'screening',
      number: 2,
      title: 'Initial Screening',
      description: 'Quick filter to identify deals worth deeper analysis.',
      actions: [
        'Run Quick Analyzer with basic assumptions',
        'Check location (arrondissement desirability)',
        'Verify rough ROI meets minimum threshold',
        'Pass or proceed to underwriting',
      ],
      tools: ['Quick Analyzer', 'Market Comparables'],
      nextStep: 'underwriting',
    },
    {
      id: 'underwriting',
      number: 3,
      title: 'Underwriting',
      description: 'Deep analysis to establish bid price and terms.',
      actions: [
        'Full deal entry with all assumptions',
        'Validate with market comparables',
        'Run scenario analysis (Conservative/Base/Aggressive)',
        'Calculate max bid and walk-away price',
        'Review AI intelligence score and risks',
      ],
      tools: ['Deal Detail', 'Market Comparables', 'AI Intelligence'],
      nextStep: 'negotiation',
    },
    {
      id: 'negotiation',
      number: 4,
      title: 'Offer & Negotiation',
      description: 'Making offers and negotiating terms.',
      actions: [
        'Submit offer at or below recommended bid',
        'Use max bid as ceiling, walk-away as floor',
        'Update deal assumptions based on negotiation',
        'Track offer status in Deal Pipeline',
      ],
      tools: ['Deal Detail', 'Quick Analyzer'],
      nextStep: 'acquisition',
    },
    {
      id: 'acquisition',
      number: 5,
      title: 'Acquisition',
      description: 'Closing the purchase and initializing renovation.',
      actions: [
        'Update deal with final acquisition price',
        'Confirm financing terms',
        'Set renovation budget and timeline',
        'Assign project manager',
        'Move to "Acquired" stage',
      ],
      tools: ['Deal Detail', 'Renovation Control', 'Partner Scorecard'],
      nextStep: 'renovation',
    },
    {
      id: 'renovation',
      number: 6,
      title: 'Renovation Tracking',
      description: 'Monitoring progress, costs, and timeline.',
      actions: [
        'Update actual costs weekly',
        'Track timeline progress',
        'Monitor budget variance alerts',
        'Recalculate economics if overruns occur',
        'Move to "Ready for Sale" when complete',
      ],
      tools: ['Renovation Control', 'Deal Detail'],
      nextStep: 'exit-prep',
    },
    {
      id: 'exit-prep',
      number: 7,
      title: 'Exit Preparation',
      description: 'Preparing property for sale.',
      actions: [
        'Set listing price based on updated comps',
        'Review AI exit recommendations',
        'Initialize Exit Tracker',
        'Set pricing thresholds (target, minimum, break-even)',
      ],
      tools: ['Exit Tracker', 'Market Comparables', 'AI Intelligence'],
      nextStep: 'marketing',
    },
    {
      id: 'marketing',
      number: 8,
      title: 'Marketing & Sale',
      description: 'Active marketing and offer management.',
      actions: [
        'Track days on market',
        'Log and evaluate offers',
        'Use holding cost analysis for price decisions',
        'Accept offer or adjust strategy',
        'Complete sale and record final results',
      ],
      tools: ['Exit Tracker', 'Deal Detail'],
      nextStep: 'review',
    },
    {
      id: 'review',
      number: 9,
      title: 'Post-Deal Review',
      description: 'Learning from results to improve future deals.',
      actions: [
        'Compare forecast vs. actual across all metrics',
        'Update partner performance scores',
        'Identify estimation biases',
        'Document lessons learned',
      ],
      tools: ['Partner Scorecard', 'Deal Detail'],
      nextStep: null,
    },
  ],
};

// ============== MODULE DEEP DIVES ==============

export const moduleDeepDives = {
  dealAnalyzer: {
    id: 'deal-analyzer-module',
    title: 'Deal Analyzer Engine',
    category: 'modules',
    purpose: 'Central calculation engine for all deal economics. Every profit, ROI, and bid recommendation flows through this module.',
    inputs: [
      'Property details (address, surface, condition)',
      'Acquisition assumptions (price, fees, financing)',
      'Renovation estimates (budget, timeline)',
      'Exit assumptions (target price, timeline)',
    ],
    outputs: [
      'Cost breakdown (acquisition, renovation, financing, holding)',
      'Profit analysis (gross/net profit, ROI, margin)',
      'Bid recommendations (max, recommended, aggressive, walk-away)',
      'Break-even analysis',
      'Scenario comparisons',
    ],
    decisionsSupported: [
      'Should I pursue this deal?',
      'What is my maximum bid?',
      'What ROI can I realistically expect?',
      'How much margin do I have for error?',
    ],
  },

  comparablesModule: {
    id: 'comparables-module',
    title: 'Market Comparables System',
    category: 'modules',
    purpose: 'Validate pricing assumptions with market evidence. Provides objective price ranges for both acquisition and exit.',
    inputs: [
      'Transaction records (sold prices, asking prices)',
      'Property attributes (location, size, condition)',
      'Date of observation',
      'Source and confidence level',
    ],
    outputs: [
      'Relevance-scored comparables for each deal',
      'Current condition price range (Low/Base/High €/m²)',
      'Renovated exit price range',
      'Comparable set strength score',
      'Acquisition gap analysis',
    ],
    decisionsSupported: [
      'Is my acquisition price below market?',
      'What is a realistic exit price?',
      'Do I have enough market evidence?',
      'Am I paying fair value or premium?',
    ],
  },

  exitTrackerModule: {
    id: 'exit-tracker-module',
    title: 'Exit Tracker System',
    category: 'modules',
    purpose: 'Optimize sale timing and pricing. Balance maximum price against holding costs and market conditions.',
    inputs: [
      'Listing details (price, date, marketing status)',
      'Offers received (amount, terms, buyer status)',
      'Holding costs (financing, maintenance, taxes)',
      'Market comp updates',
    ],
    outputs: [
      'Days on market tracking',
      'Offer analysis and recommendations',
      'Holding cost impact',
      'Price reduction recommendations',
      'Break-even timeline',
    ],
    decisionsSupported: [
      'Should I accept this offer?',
      'When should I reduce price?',
      'How much is waiting costing me?',
      'What is my minimum acceptable price today?',
    ],
  },

  partnerScorecardModule: {
    id: 'partner-scorecard-module',
    title: 'Partner Scorecard System',
    category: 'modules',
    purpose: 'Evaluate partner reliability and performance. Make data-driven decisions about who to work with.',
    inputs: [
      'Partner deal history',
      'Forecast vs. actual comparisons',
      'Budget adherence records',
      'Timeline performance',
    ],
    outputs: [
      'Composite performance score (0-100)',
      'Score breakdown by category',
      'Risk signals and warnings',
      'Recommendations (scale up, maintain, reduce exposure)',
      'Portfolio comparison and ranking',
    ],
    decisionsSupported: [
      'Which sourcer should I prioritize?',
      'Is this project manager reliable?',
      'Should I negotiate fees based on performance?',
      'Who is adding risk to my portfolio?',
    ],
  },

  aiScoringModule: {
    id: 'ai-scoring-module',
    title: 'AI Intelligence System',
    category: 'modules',
    purpose: 'Synthesize all data into actionable scores and recommendations. Surface risks and opportunities that manual review might miss.',
    inputs: [
      'Complete deal data',
      'Comparable analysis',
      'Partner performance',
      'Historical patterns',
    ],
    outputs: [
      'Deal Score (overall quality 0-100)',
      'Risk Score (aggregate risk level)',
      'Confidence Score (analysis reliability)',
      'Buy/Negotiate/Pass recommendation',
      'Attention flags and priority actions',
      'AI-generated deal memo',
    ],
    decisionsSupported: [
      'Should I proceed with this deal?',
      'What are the biggest risks?',
      'How confident should I be in the analysis?',
      'What should I focus on?',
    ],
  },

  dataIngestionModule: {
    id: 'data-ingestion-module',
    title: 'Data Ingestion System',
    category: 'modules',
    purpose: 'Build and maintain your market data foundation. Import, normalize, validate, and deduplicate external data.',
    inputs: [
      'CSV exports from listing sites',
      'Partner-provided data',
      'Manual data entry',
      'DVF (official transaction records)',
    ],
    outputs: [
      'Normalized, validated records',
      'Duplicate detection',
      'Confidence scoring',
      'Review queue for approval',
      'Verified comparables',
    ],
    decisionsSupported: [
      'Is this data reliable enough to use?',
      'Do I already have this record?',
      'Should I approve this for analysis?',
      'What is the quality of my data foundation?',
    ],
  },
};

// ============== DECISION FRAMEWORKS ==============

export const decisionFrameworks = {
  buyDecision: {
    id: 'buy-decision',
    title: 'Decision: When to Buy',
    category: 'frameworks',
    tags: ['buy', 'decision', 'criteria', 'checklist'],
    content: `**Strong Buy Signals:**
- Deal Score ≥75, Risk Score ≤30
- Conservative scenario still profitable (>8% ROI)
- Acquisition price below comp-supported value
- 3+ relevant sold comps supporting exit price
- Proven partner with good track record

**Proceed with Caution:**
- Deal Score 60-75, Risk Score 30-50
- Base case profitable, conservative break-even
- Limited comp support (2 comps or asking-only)
- New or unproven partner

**Pass Signals:**
- Risk Score ≥70 (High/Critical)
- Only aggressive scenario profitable
- No sold comp support for exit assumptions
- Partner has history of overruns
- Confidence Score <40 (insufficient data)`,
  },

  priceNegotiation: {
    id: 'price-negotiation',
    title: 'Decision: Price Negotiation',
    category: 'frameworks',
    tags: ['negotiate', 'bid', 'price', 'strategy'],
    content: `**Before Negotiating:**
1. Know your Max Bid (ceiling you won't exceed)
2. Know your Walk-away price (floor below which deal doesn't work)
3. Know comp-supported acquisition value

**Negotiation Strategy:**
- Start below recommended bid (room to move up)
- Use comp evidence to justify your position
- Highlight required renovation scope
- Reference recent sold prices, not asking

**When to Hold Firm:**
- At or near max bid already
- Seller motivated (timeline pressure)
- Limited buyer competition

**When to Walk Away:**
- Seller won't move below your walk-away
- Due diligence reveals hidden problems
- Comps don't support required entry price`,
  },

  exitAssumptionValidation: {
    id: 'exit-validation',
    title: 'Decision: Validating Exit Assumptions',
    category: 'frameworks',
    tags: ['exit', 'validate', 'assumptions', 'reality-check'],
    content: `**Strong Exit Assumptions:**
- 3+ sold comps within 12 months
- Your exit price within comp €/m² range
- Similar condition (renovated comps for renovated exit)
- Same arrondissement, similar size

**Warning Signs:**
- Exit price above highest comp
- Only asking price comps (not sold)
- Comps older than 12 months
- Comparing to different areas or conditions

**Reality Check Process:**
1. Run comp analysis for target arrondissement
2. Filter to similar surface (±20%)
3. Filter to similar condition
4. Calculate €/m² range from sold transactions
5. Apply to your surface for price range
6. Your exit assumption should be within base-high range`,
  },

  partnerEvaluation: {
    id: 'partner-evaluation',
    title: 'Decision: Evaluating Partner Performance',
    category: 'frameworks',
    tags: ['partner', 'evaluate', 'performance', 'decision'],
    content: `**Top Performer (Score ≥85):**
- Scale up allocation
- Priority deal assignment
- Consider reducing fee for volume commitment

**Strong Performer (Score 70-84):**
- Maintain current relationship
- Monitor for consistency
- Address any specific weak areas

**Average Performer (Score 50-69):**
- Limit exposure to smaller deals
- Require more oversight
- Set improvement expectations

**Weak/High Risk (Score <50):**
- Reduce or eliminate allocation
- Review existing deals closely
- Consider replacement

**Key Metrics to Watch:**
- Forecast accuracy (are estimates reliable?)
- Budget adherence (can they control costs?)
- Timeline discipline (do they deliver on time?)
- Consistency (reliable or volatile?)`,
  },
};

// ============== SEARCH INDEX ==============

export const searchIndex = [
  // Pages
  ...Object.values(pageExplanations).map(p => ({
    type: 'page',
    id: p.id,
    title: p.title,
    keywords: [...p.tags, p.title.toLowerCase()],
    description: p.what,
    category: 'Pages',
  })),
  
  // Concepts
  ...Object.values(keyConcepts).map(c => ({
    type: 'concept',
    id: c.id,
    title: c.title,
    keywords: [...c.tags, c.title.toLowerCase()],
    description: c.shortDescription,
    category: 'Key Concepts',
  })),
  
  // Modules
  ...Object.values(moduleDeepDives).map(m => ({
    type: 'module',
    id: m.id,
    title: m.title,
    keywords: [m.title.toLowerCase(), ...m.purpose.toLowerCase().split(' ').filter(w => w.length > 4)],
    description: m.purpose,
    category: 'Modules',
  })),
  
  // Decision Frameworks
  ...Object.values(decisionFrameworks).map(d => ({
    type: 'framework',
    id: d.id,
    title: d.title,
    keywords: [...d.tags, d.title.toLowerCase()],
    description: d.content.slice(0, 100) + '...',
    category: 'Decision Frameworks',
  })),
  
  // Workflow steps
  ...investmentWorkflow.steps.map(s => ({
    type: 'workflow',
    id: s.id,
    title: `Workflow: ${s.title}`,
    keywords: [s.title.toLowerCase(), 'workflow', 'step', 'process'],
    description: s.description,
    category: 'Investment Workflow',
  })),
];

// ============== SYNONYMS FOR SEARCH ==============

export const searchSynonyms = {
  profit: ['margin', 'return', 'gain', 'earnings'],
  roi: ['return', 'yield', 'percentage'],
  comps: ['comparables', 'transactions', 'market data'],
  buy: ['acquire', 'purchase', 'bid'],
  sell: ['exit', 'sale', 'dispose'],
  renovation: ['rehab', 'refurbishment', 'construction', 'works'],
  partner: ['sourcer', 'manager', 'contractor'],
  risk: ['danger', 'warning', 'concern', 'problem'],
};

export default {
  pageExplanations,
  keyConcepts,
  investmentWorkflow,
  moduleDeepDives,
  decisionFrameworks,
  searchIndex,
  searchSynonyms,
};
